// SPDX-FileCopyrightText: 2026 Mads Ringblom
//
// SPDX-License-Identifier: GPL-3.0-or-later

/**
 * The building parts the logo's X turns into, one per play: window, door,
 * chair, brick pier, pendant lamp. Each is a density over the unit box (u
 * right, v down; 0 = empty, 1 = solid) inside its own `box`, which the points
 * module fits into the X's box. Colours are material tokens, read at draw
 * time so they follow the theme. DOM-free.
 */

export interface LogoPart {
  /** Danish name (not shown; for debugging and tests). */
  name: string;
  /** The part's extent in the unit box: [u0, v0, u1, v1]. */
  box: readonly [number, number, number, number];
  /** How likely a point at (u, v) belongs to the part, 0..1. */
  density: (u: number, v: number) => number;
  /** The custom property the points tint towards while transformed. */
  colorToken: string;
}

const rIn = (u: number, v: number, a: number, b: number, c: number, d: number) => u >= a && u <= c && v >= b && v <= d;
const frIn = (u: number, v: number, a: number, b: number, c: number, d: number, t: number) =>
  rIn(u, v, a, b, c, d) && !rIn(u, v, a + t, b + t, c - t, d - t);

/** The pendant's three shade tiers: [top, bottom, half-width at the rim]. */
const LAMP_TIERS: readonly (readonly [number, number, number])[] = [
  [0.34, 0.45, 0.17],
  [0.48, 0.61, 0.24],
  [0.64, 0.78, 0.3],
];

export const PARTS: readonly LogoPart[] = [
  {
    name: 'Vindue',
    box: [0.17, 0.04, 0.83, 0.96],
    // A tall casement window: frame, a high transom with a top light, two sashes below it, a deep sill.
    density: (u, v) =>
      frIn(u, v, 0.22, 0.04, 0.78, 0.9, 0.045) ||
      rIn(u, v, 0.22, 0.3, 0.78, 0.335) ||
      rIn(u, v, 0.483, 0.335, 0.517, 0.9) ||
      rIn(u, v, 0.17, 0.9, 0.83, 0.96)
        ? 1
        : rIn(u, v, 0.22, 0.04, 0.78, 0.9)
          ? 0.045
          : 0,
    colorToken: '--mat-glas',
  },
  {
    name: 'Dør',
    box: [0.28, 0.04, 0.72, 0.96],
    // A panelled door: frame, two raised panels, a handle.
    density: (u, v) =>
      frIn(u, v, 0.28, 0.04, 0.72, 0.96, 0.05) ||
      frIn(u, v, 0.37, 0.14, 0.63, 0.46, 0.025) ||
      frIn(u, v, 0.37, 0.54, 0.63, 0.86, 0.025) ||
      Math.hypot(u - 0.655, v - 0.5) < 0.022
        ? 1
        : rIn(u, v, 0.28, 0.04, 0.72, 0.96)
          ? 0.07
          : 0,
    colorToken: '--mat-trae',
  },
  {
    name: 'Stol',
    box: [0.3, 0.08, 0.75, 0.92],
    // A chair in profile: back with two rails, seat, legs, a stretcher.
    density: (u, v) =>
      rIn(u, v, 0.3, 0.08, 0.37, 0.92) ||
      rIn(u, v, 0.3, 0.5, 0.75, 0.57) ||
      rIn(u, v, 0.68, 0.57, 0.75, 0.92) ||
      rIn(u, v, 0.3, 0.22, 0.44, 0.26) ||
      rIn(u, v, 0.3, 0.32, 0.44, 0.36) ||
      rIn(u, v, 0.37, 0.76, 0.68, 0.79)
        ? 1
        : 0,
    colorToken: '--mat-metal',
  },
  {
    name: 'Mursten',
    box: [0.2, 0.04, 0.8, 0.96],
    // A brick pier the size of the X: seven courses in running bond, two bricks wide.
    density: (u, v) => {
      if (!rIn(u, v, 0.2, 0.04, 0.8, 0.96)) return 0;
      const rh = 0.92 / 7;
      const row = Math.floor((v - 0.04) / rh);
      const lv = v - 0.04 - row * rh;
      if (lv < 0.032) return 0;
      const bl = 0.3;
      const x = (u - 0.2 + (row % 2 ? bl / 2 : 0) + bl) % bl;
      return x < 0.032 ? 0 : 0.95;
    },
    colorToken: '--mat-tegl',
  },
  {
    name: 'Lampe',
    box: [0.2, 0.04, 0.8, 0.92],
    // A PH-style pendant: cord, three stacked curved shades, the bulb glowing below.
    density: (u, v) => {
      const dx = Math.abs(u - 0.5);
      if (dx < 0.012 && v >= 0.04 && v <= 0.4) return 1;
      if (Math.hypot(u - 0.5, v - 0.87) < 0.03) return 1;
      // Three separate tiers with open gaps between them; each flares out towards its rim.
      for (const [v0, v1, h] of LAMP_TIERS) {
        if (v < v0 || v > v1) continue;
        const t = (v - v0) / (v1 - v0);
        const hw = h * (0.55 + 0.45 * Math.sqrt(t));
        // The shade is a shell: a curved band following its own profile, open underneath.
        const top = v0 + (v1 - v0) * Math.pow(dx / h, 2) * 0.9;
        if (dx <= hw && v <= top + 0.045) return 0.95;
      }
      return 0;
    },
    colorToken: '--mat-mineraluld',
  },
];
